/**
 * Banner de cupo en pre-finalize.
 *
 * Llama a ``GET /api/customer-drafts/{draftId}/finalize-preview`` y
 * muestra al usuario, ANTES de que apriete "Finalizar", cuántos
 * onboardings / screenings / PDFs va a consumir su expediente moral —
 * más cualquier blocker concreto del plan y warnings de vigencia
 * documental (CSF / comprobante).
 *
 * Diseño explícito:
 *  - Verde-teal: cuota OK, sólo informa.
 *  - Ámbar: hay warnings (vigencia) pero finalize permitido.
 *  - Rojo: blockers de plan; muestra detalle accionable + link a /facturacion.
 *    El botón "Finalizar" del padre se debe deshabilitar via la prop ``onQuota``.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FinalizePreviewBanner({ draftId, onQuotaChange }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!draftId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/api/customer-drafts/${draftId}/finalize-preview`,
          { withCredentials: true },
        );
        if (!alive) return;
        setData(r.data);
        onQuotaChange?.({ ok: r.data?.quota?.ok ?? true, raw: r.data });
      } catch (e) {
        if (!alive) return;
        setErr(e.response?.data?.detail || "No se pudo calcular el cupo del expediente");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [draftId, onQuotaChange]);

  if (loading) {
    return (
      <div
        data-testid="finalize-preview-loading"
        className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400"
      >
        Calculando el costo de este expediente en tu plan…
      </div>
    );
  }
  if (err) {
    return (
      <div
        data-testid="finalize-preview-error"
        className="rounded-xl border border-rose-700/40 bg-rose-950/30 p-4 text-sm text-rose-300"
      >
        <AlertTriangle className="inline-block w-4 h-4 -mt-1 mr-2" />
        {err}
      </div>
    );
  }
  if (!data) return null;

  const { quota, subjects, warnings } = data;
  const blockers = quota?.blockers || [];
  const isMoral = data.person_type === "moral";
  const tone = blockers.length > 0
    ? "rose"
    : (warnings && warnings.length > 0 ? "amber" : "teal");

  const toneClasses = {
    teal:  { border: "border-teal-700/40",  bg: "bg-teal-950/30",  text: "text-teal-200",  icon: ShieldCheck, iconClr: "text-teal-400" },
    amber: { border: "border-amber-700/40", bg: "bg-amber-950/30", text: "text-amber-200", icon: AlertTriangle, iconClr: "text-amber-400" },
    rose:  { border: "border-rose-700/40",  bg: "bg-rose-950/30",  text: "text-rose-200",  icon: AlertTriangle, iconClr: "text-rose-400" },
  }[tone];
  const Icon = toneClasses.icon;

  return (
    <div
      data-testid="finalize-preview-banner"
      className={`rounded-xl border ${toneClasses.border} ${toneClasses.bg} p-5 space-y-4`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${toneClasses.iconClr}`} />
        <div className={`flex-1 ${toneClasses.text}`}>
          <div className="font-semibold text-base">
            {blockers.length > 0
              ? "Tu plan no cubre este expediente todavía"
              : (isMoral ? "Resumen del expediente que vas a crear" : "Listo para finalizar")}
          </div>
          <div className="mt-2 text-sm space-y-1">
            <div>
              <Sparkles className="inline-block w-3.5 h-3.5 -mt-0.5 mr-1" />
              Consumo: <strong>{quota.onboardings_needed}</strong> onboarding ·{" "}
              <strong>{quota.screenings_needed}</strong> screening{quota.screenings_needed !== 1 ? "s" : ""} PEP/OFAC ·{" "}
              <strong>{quota.pdfs_needed}</strong> PDF
              {quota.ocr_already_used > 0 ? (
                <> · <strong>{quota.ocr_already_used}</strong> extracciones OCR ya usadas</>
              ) : null}
            </div>
            {isMoral && (subjects?.representatives?.length > 0 || subjects?.beneficial_owners?.length > 0) && (
              <div className="text-xs opacity-90 pt-1">
                Sujetos a verificar:{" "}
                {subjects.representatives.map((r, i) => (
                  <span key={`r-${i}`} className="mr-2">
                    {r.label}{r.full_name ? `: ${r.full_name}` : ""}
                  </span>
                ))}
                {subjects.beneficial_owners.map((b, i) => (
                  <span key={`b-${i}`} className="mr-2">
                    Beneficiario: {b.full_name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {blockers.length > 0 && (
        <ul data-testid="finalize-preview-blockers" className="space-y-2 pl-8 text-sm">
          {blockers.map((b) => (
            <li key={b.quota} className="text-rose-200/90">
              <div className="font-medium">{b.label}: {b.current}/{b.max} usados, este expediente requiere {b.needed}.</div>
              <div className="text-rose-300/80 text-xs">{b.detail}</div>
            </li>
          ))}
          <li className="pt-2">
            <Link
              to="/facturacion"
              className="inline-flex items-center gap-1 text-rose-100 underline underline-offset-4 decoration-rose-400/60 hover:text-white"
            >
              Mejorar plan <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </li>
        </ul>
      )}

      {warnings && warnings.length > 0 && (
        <div data-testid="finalize-preview-warnings" className="pl-8 text-sm space-y-1">
          <div className="text-amber-200/90 font-medium">
            Avisos de vigencia documental ({warnings.length}):
          </div>
          <ul className="text-amber-200/80 text-xs space-y-1">
            {warnings.map((w, i) => (
              <li key={i}>• {w.label}</li>
            ))}
          </ul>
          <div className="text-amber-200/60 text-xs pt-1">
            Estos avisos no impiden finalizar; quedan registrados como evidencia de revisión.
          </div>
        </div>
      )}
    </div>
  );
}
